import React, { useState } from "react";
import "./CustomerIdentity.css";

const API_URL = `${process.env.REACT_APP_URL}customer_identity/submit`;

const CustomerIdentity = () => {
  const [identityStatus, setIdentityStatus] = useState("");

  const [formData, setFormData] = useState({
    account_holder_name: "",
    account_number: "",
    date_of_birth: "",
    nid_number: "",
    email: "",
  });

  const [files, setFiles] = useState({
    nid_front_file: null,
    nid_back_file: null,
    citizenship_file: null,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [apiError, setApiError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setApiError("");
  };

  const handleIdentityStatus = (status) => {
    setIdentityStatus(status);

    setFormData({
      account_holder_name: "",
      account_number: "",
      date_of_birth: "",
      nid_number: "",
      email: "",
    });

    setFiles({
      nid_front_file: null,
      nid_back_file: null,
      citizenship_file: null,
    });

    setErrors({});
    setApiError("");
    setSuccessData(null);
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    const file = selectedFiles?.[0];

    if (!file) return;

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setApiError("");

    if (file.size > 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        [name]: "File size must not exceed 1 MB.",
      }));

      e.target.value = "";
      return;
    }

    const allowedExtensions = ["jpg", "jpeg", "png", "pdf"];
    const extension = file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      setErrors((prev) => ({
        ...prev,
        [name]: "Only JPG, JPEG, PNG and PDF files are allowed.",
      }));

      e.target.value = "";
      return;
    }

    setFiles((prev) => ({
      ...prev,
      [name]: file,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!identityStatus) {
      newErrors.identity_status = "Please select an identity option.";
    }

    if (!formData.account_holder_name.trim()) {
      newErrors.account_holder_name = "Account holder name is required.";
    }

    // Account number is required for BOTH identity options
    // if (!formData.account_number.trim()) {
    //   newErrors.account_number = "Account number is required.";
    // }

    if (!formData.account_number.trim()) {
      newErrors.account_number = "Account number is required.";
    } else if (!/^\d{14}$/.test(formData.account_number.trim())) {
      newErrors.account_number = "Account number must be exactly 14 digits.";
    }

    // if (!formData.email.trim()) {
    //   newErrors.email = "Email address is required.";
    // } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    //   newErrors.email = "Enter a valid email address.";
    // }
    // Email is optional
    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    ) {
      newErrors.email = "Enter a valid email address.";
    }

    if (identityStatus === "NID_AVAILABLE") {
      if (!formData.date_of_birth) {
        newErrors.date_of_birth = "Date of birth is required.";
      }

      if (!formData.nid_number.trim()) {
        newErrors.nid_number = "NID number is required.";
      }

      if (!files.nid_front_file) {
        newErrors.nid_front_file = "NID front side document is required.";
      }

      if (!files.nid_back_file) {
        newErrors.nid_back_file = "NID back side document is required.";
      }
    }

    if (identityStatus === "NID_NOT_AVAILABLE") {
      if (!files.citizenship_file) {
        newErrors.citizenship_file = "Citizenship document is required.";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSuccessData(null);
    setApiError("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      data.append("identity_status", identityStatus);
      data.append("account_holder_name", formData.account_holder_name.trim());
      data.append("account_number", formData.account_number.trim());
      data.append("email", formData.email.trim());

      if (identityStatus === "NID_AVAILABLE") {
        data.append("date_of_birth", formData.date_of_birth);
        data.append("nid_number", formData.nid_number.trim());
        data.append("nid_front_file", files.nid_front_file);
        data.append("nid_back_file", files.nid_back_file);
      }

      if (identityStatus === "NID_NOT_AVAILABLE") {
        data.append("citizenship_file", files.citizenship_file);
      }

      const response = await fetch(API_URL, {
        method: "POST",
        body: data,
      });

      const result = await response.json();

      console.log("API Response:", result);

      // Backend returns: status = "success"
      if (!response.ok || result.status !== "success") {
        throw new Error(result.status_message || "Unable to submit the form.");
      }

      // SUCCESS
      setSuccessData({
        message:
          result.status_message || "Identity details submitted successfully.",
        reference_no: result.data?.reference_no || "",
      });

      setApiError("");

      // Reset form
      setIdentityStatus("");

      setFormData({
        account_holder_name: "",
        account_number: "",
        date_of_birth: "",
        nid_number: "",
        email: "",
      });

      setFiles({
        nid_front_file: null,
        nid_back_file: null,
        citizenship_file: null,
      });

      setErrors({});

      document.querySelectorAll('input[type="file"]').forEach((input) => {
        input.value = "";
      });
    } catch (error) {
      console.error("Customer Identity Error:", error);

      setSuccessData(null);

      setApiError(error.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="customer-identity-page">
      <div className="container py-10">
        <div className="row justify-content-center">
          <div className="col-xl-8 col-lg-9 col-md-11">
            <div className="text-center mb-4">
              <h2 className="fw-bold">Customer Identity Update</h2>

              <p className="text-muted mb-0">
                Please provide your updated identity information.
              </p>
            </div>

            {(successData || apiError) && (
              <div
                className={`identity-toast ${
                  successData
                    ? "identity-toast-success"
                    : "identity-toast-error"
                }`}
              >
                <div className="identity-toast-icon">
                  {successData ? "✓" : "!"}
                </div>

                <div className="identity-toast-content">
                  <strong>
                    {successData
                      ? "Submission Successful"
                      : "Submission Failed"}
                  </strong>

                  <div>{successData ? successData.message : apiError}</div>

                  {successData?.reference_no && (
                    <small>
                      Reference No: <strong>{successData.reference_no}</strong>
                    </small>
                  )}
                </div>

                <button
                  type="button"
                  className="identity-toast-close"
                  onClick={() => {
                    setSuccessData(null);
                    setApiError("");
                  }}
                >
                  ×
                </button>
              </div>
            )}

            <div className="card border-0 shadow-sm">
              <div className="card-body p-4 p-md-5">
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Do you have a National Identity Card (NID)?
                  </label>

                  {errors.identity_status && (
                    <div className="text-danger small mb-2">
                      {errors.identity_status}
                    </div>
                  )}

                  <div className="row g-3">
                    <div className="col-md-6">
                      <button
                        type="button"
                        className={`identity-option ${
                          identityStatus === "NID_AVAILABLE" ? "active" : ""
                        }`}
                        onClick={() => handleIdentityStatus("NID_AVAILABLE")}
                      >
                        <span className="option-icon">✓</span>

                        <span>
                          <strong>NID Available</strong>

                          <small>I have a National Identity Card</small>
                        </span>
                      </button>
                    </div>

                    <div className="col-md-6">
                      <button
                        type="button"
                        className={`identity-option ${
                          identityStatus === "NID_NOT_AVAILABLE" ? "active" : ""
                        }`}
                        onClick={() =>
                          handleIdentityStatus("NID_NOT_AVAILABLE")
                        }
                      >
                        <span className="option-icon">✓</span>

                        <span>
                          <strong>NID Not Available</strong>

                          <small>I do not have NID</small>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {identityStatus && (
                  <form onSubmit={handleSubmit} noValidate>
                    {/* Account Holder Name */}
                    <div className="mb-3">
                      <label className="form-label">
                        Account Holder Name
                        <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        name="account_holder_name"
                        className={`form-control ${
                          errors.account_holder_name ? "is-invalid" : ""
                        }`}
                        placeholder="Enter account holder name"
                        value={formData.account_holder_name}
                        onChange={handleChange}
                        maxLength={150}
                        autoComplete="name"
                      />

                      {errors.account_holder_name && (
                        <div className="invalid-feedback">
                          {errors.account_holder_name}
                        </div>
                      )}
                    </div>

                    {/* Account Number - Common for both modes */}
                    <div className="mb-3">
                      <label className="form-label">
                        Account Number
                        <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        inputMode="numeric"
                        name="account_number"
                        className={`form-control ${
                          errors.account_number ? "is-invalid" : ""
                        }`}
                        placeholder="Enter 14 digit account number"
                        value={formData.account_number}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 14);

                          setFormData((prev) => ({
                            ...prev,
                            account_number: value,
                          }));

                          setErrors((prev) => ({
                            ...prev,
                            account_number: "",
                          }));

                          setApiError("");
                        }}
                        maxLength={14}
                        autoComplete="off"
                      />

                      {errors.account_number && (
                        <div className="invalid-feedback">
                          {errors.account_number}
                        </div>
                      )}
                    </div>

                    {/* NID Available */}
                    {identityStatus === "NID_AVAILABLE" && (
                      <>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Date of Birth
                              <span className="text-danger">*</span>
                            </label>

                            <input
                              type="date"
                              name="date_of_birth"
                              className={`form-control ${
                                errors.date_of_birth ? "is-invalid" : ""
                              }`}
                              value={formData.date_of_birth}
                              onChange={handleChange}
                              max={new Date().toISOString().split("T")[0]}
                            />

                            {errors.date_of_birth && (
                              <div className="invalid-feedback">
                                {errors.date_of_birth}
                              </div>
                            )}
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              NID Number
                              <span className="text-danger">*</span>
                            </label>

                            <input
                              type="text"
                              name="nid_number"
                              className={`form-control ${
                                errors.nid_number ? "is-invalid" : ""
                              }`}
                              placeholder="Enter NID number"
                              value={formData.nid_number}
                              onChange={handleChange}
                              maxLength={50}
                            />

                            {errors.nid_number && (
                              <div className="invalid-feedback">
                                {errors.nid_number}
                              </div>
                            )}
                          </div>
                        </div>

                        <FileUpload
                          label="NID Front Side"
                          name="nid_front_file"
                          file={files.nid_front_file}
                          error={errors.nid_front_file}
                          onChange={handleFileChange}
                        />

                        <FileUpload
                          label="NID Back Side"
                          name="nid_back_file"
                          file={files.nid_back_file}
                          error={errors.nid_back_file}
                          onChange={handleFileChange}
                        />
                      </>
                    )}

                    {/* NID Not Available */}
                    {identityStatus === "NID_NOT_AVAILABLE" && (
                      <>
                        <FileUpload
                          label="Original Citizenship"
                          name="citizenship_file"
                          file={files.citizenship_file}
                          error={errors.citizenship_file}
                          onChange={handleFileChange}
                        />
                      </>
                    )}

                    {/* Email */}
                    <div className="mb-4">
                      <label className="form-label">
                        Email Address
                        <small className="text-muted ms-1">(Optional)</small>
                      </label>

                      <input
                        type="email"
                        name="email"
                        className={`form-control ${
                          errors.email ? "is-invalid" : ""
                        }`}
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={handleChange}
                        maxLength={150}
                        autoComplete="email"
                      />

                      {errors.email && (
                        <div className="invalid-feedback">{errors.email}</div>
                      )}
                    </div>

                    {/* Security Notice */}
                    <div className="security-notice mb-4">
                      <div className="security-icon">🔒</div>

                      <div>
                        <strong>Your information is secure</strong>

                        <p>
                          Your identity documents will be securely stored and
                          used only for customer record updates.
                        </p>
                      </div>
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      className="btn btn-primary w-100 py-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          Submitting...
                        </>
                      ) : (
                        "Submit Identity Details"
                      )}
                    </button>
                  </form>
                )}

                {!identityStatus && !successData && (
                  <div className="text-center py-4 text-muted">
                    <div className="fs-1 mb-2">🪪</div>

                    <p className="mb-0">Select an option above to continue.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="text-center mt-3">
              <small className="text-muted">
                Maximum document size: 1 MB. Allowed formats: JPG, JPEG, PNG and
                PDF.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FileUpload = ({ label, name, file, error, onChange }) => {
  return (
    <div className="mb-4">
      <label className="form-label">
        {label}
        <span className="text-danger">*</span>
      </label>

      <div className={`upload-box ${error ? "upload-error" : ""}`}>
        <input
          type="file"
          name={name}
          id={name}
          className="d-none"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={onChange}
        />

        <label htmlFor={name} className="upload-label">
          <div className="upload-icon">📄</div>

          <div>
            {file ? (
              <>
                <strong>{file.name}</strong>

                <small>Click to replace file</small>
              </>
            ) : (
              <>
                <strong>Click to upload</strong>

                <small>JPG, JPEG, PNG or PDF (Maximum 1 MB)</small>
              </>
            )}
          </div>
        </label>
      </div>

      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

export default CustomerIdentity;
